/**
 * IceScoreBadge
 *
 * The ICE flag on a scope row. Three states: a score, an evaluation in
 * progress, or never scored.
 *
 * Deliberately neutral in colour. The band (`strong` / `worth_pursuing` /
 * `needs_work` / `park`) is the backend's mapping to make, and a red-to-green
 * badge would also read as a verdict — which ICE is not. The band label lives
 * inside the details popup, where the reasoning is there to qualify it.
 *
 * @module @ideoz/ui/components/molecules/IceScoreBadge
 */

import React from 'react';
import { Gauge, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { IceScoreBadgeProps } from '../../types/Ice.types';

const BASE =
  'inline-flex items-center gap-0.5 rounded-xl border px-2.5 py-1 text-xs font-regular transition-colors';

export const IceScoreBadge: React.FC<IceScoreBadgeProps> = ({
  ice = null,
  isScoring = false,
  onClick,
  className = '',
  testId = 'ice-score-badge',
}) => {
  if (isScoring) {
    return (
      <span
        data-testid={testId}
        data-ice-state="scoring"
        className={cn(
          BASE,
          'border-success-200 bg-success-50 text-primary-700 dark:border-primary-700 dark:bg-primary-950/60 dark:text-success-900',
          className
        )}
        role="status"
      >
        <Loader2 className="h-3 w-3 animate-spin text-primary-500" strokeWidth={2} aria-hidden />
        Scoring…
      </span>
    );
  }

  if (ice == null) {
    return (
      <span
        data-testid={testId}
        data-ice-state="not-scored"
        className={cn(
          BASE,
          'border-dashed border-gray-200 bg-transparent dark:border-gray-700',
          className
        )}
      >
        Not scored
      </span>
    );
  }

  const content = (
    <>
      <span>ICE</span>
      <span className="font-medium">{ice.toFixed(1)}</span>
    </>
  );

  if (!onClick) {
    return (
      <span
        data-testid={testId}
        data-ice-state="scored"
        className={cn(
          BASE,
          'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-750',
          className
        )}
      >
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      data-ice-state="scored"
      onClick={onClick}
      title="See how this score was reached"
      className={cn(
        BASE,
        'cursor-pointer border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 dark:border-blue-700 dark:bg-blue-750 dark:hover:bg-blue-700',
        className
      )}
    >
      <Gauge className="h-4 w-4 me-0.5" />
      {content}
    </button>
  );
};

export default IceScoreBadge;
